import unescapedText from 'I18n/utils/unescapedText';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { H5 } from 'foundations-components/transitional/components/H5';
import { Paragraph } from 'foundations-components/transitional/components/Paragraph';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { TextInput } from 'foundations-components/transitional/components/TextInput';
import { View } from 'foundations-components/transitional/components/View';
import { HR } from 'foundations-components/transitional/components/HR';
import { Flex } from 'foundations-components/components/Flex';
import { Fieldset } from 'foundations-components/transitional/components/Fieldset';
// @ts-expect-error untyped module
import HubSpotLanguageSelect from 'ui-addon-i18n/components/HubSpotLanguageSelect';
import { useProfileFieldValues } from '../hooks/useProfileFieldValues';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const EMPTY_PLACEHOLDER = '\u2014';
export function AccountAndProfileSection({
  loginVerifyData,
  userSettings,
  loading,
  isPreferredLanguageEnabled,
  isEditable,
  displayLanguage,
  isLanguageDisabled,
  languageError,
  pendingProfile,
  showRequiredErrors,
  onLanguageChange,
  onProfileChange
}) {
  const fields = useProfileFieldValues(pendingProfile, userSettings, loginVerifyData);
  if (loading) {
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsxs(Flex, {
        flexDirection: "column",
        role: "status",
        "aria-label": unescapedText('app.myAccountPopover.settings.loadingLabel'),
        children: [/*#__PURE__*/_jsxs(Flex, {
          flexDirection: "column",
          gap: "200",
          marginBlock: "300",
          children: [/*#__PURE__*/_jsx(View, {
            backgroundColor: "fill-accent-neutral-subtle",
            borderRadius: "100",
            inlineSize: "100px",
            blockSize: "18px"
          }), /*#__PURE__*/_jsx(View, {
            backgroundColor: "fill-accent-neutral-subtle",
            borderRadius: "100",
            inlineSize: "100%",
            blockSize: "32px"
          }), /*#__PURE__*/_jsx(View, {
            backgroundColor: "fill-accent-neutral-subtle",
            borderRadius: "100",
            inlineSize: "100%",
            blockSize: "32px"
          })]
        }), /*#__PURE__*/_jsx(HR, {
          distance: "flush"
        }), /*#__PURE__*/_jsxs(Flex, {
          flexDirection: "column",
          gap: "200",
          marginBlock: "300",
          children: [/*#__PURE__*/_jsx(View, {
            backgroundColor: "fill-accent-neutral-subtle",
            borderRadius: "100",
            inlineSize: "100px",
            blockSize: "18px"
          }), /*#__PURE__*/_jsx(View, {
            backgroundColor: "fill-accent-neutral-subtle",
            borderRadius: "100",
            inlineSize: "100%",
            blockSize: "32px"
          }), /*#__PURE__*/_jsx(View, {
            backgroundColor: "fill-accent-neutral-subtle",
            borderRadius: "100",
            inlineSize: "100%",
            blockSize: "32px"
          }), /*#__PURE__*/_jsx(View, {
            backgroundColor: "fill-accent-neutral-subtle",
            borderRadius: "100",
            inlineSize: "100%",
            blockSize: "32px"
          })]
        })]
      }), /*#__PURE__*/_jsx(HR, {
        distance: "flush"
      })]
    });
  }
  if (!loginVerifyData) {
    return null;
  }
  const email = loginVerifyData.memberEmail || loginVerifyData.email || EMPTY_PLACEHOLDER;
  const {
    firstName,
    lastName,
    jobTitle,
    phone,
    company
  } = fields;
  const val = v => v || (isEditable ? '' : EMPTY_PLACEHOLDER);
  const isMissing = v => showRequiredErrors && !v.trim();
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsxs(Flex, {
      flexDirection: "column",
      marginBlockEnd: "300",
      children: [/*#__PURE__*/_jsx(H5, {
        color: "text-core-default",
        marginBlockStart: "300",
        marginBlockEnd: "200",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "app.myAccountPopover.settings.account.title"
        })
      }), /*#__PURE__*/_jsx(View, {
        marginBlockEnd: "300",
        children: /*#__PURE__*/_jsx(Fieldset, {
          _size: "small",
          children: /*#__PURE__*/_jsx(FormControl, {
            label: unescapedText('app.myAccountPopover.settings.account.membershipEmailLabel'),
            help: unescapedText('app.myAccountPopover.settings.account.membershipEmailTooltip'),
            children: /*#__PURE__*/_jsx(TextInput, {
              name: "membershipEmail",
              value: email,
              typography: "body-100",
              readOnly: true,
              disabled: true
            })
          })
        })
      }), /*#__PURE__*/_jsx(View, {
        marginBlockEnd: "300",
        children: /*#__PURE__*/_jsx(Fieldset, {
          _size: "small",
          children: /*#__PURE__*/_jsx(FormControl, {
            label: unescapedText('app.myAccountPopover.settings.account.companyLabel'),
            children: /*#__PURE__*/_jsx(TextInput, {
              name: "company",
              value: val(company),
              typography: "body-100",
              readOnly: !isEditable,
              disabled: !isEditable,
              onChange: isEditable ? e => onProfileChange('company', e.target.value) : undefined
            })
          })
        })
      })]
    }), /*#__PURE__*/_jsx(HR, {
      distance: "flush"
    }), /*#__PURE__*/_jsxs(Flex, {
      flexDirection: "column",
      marginBlockStart: "300",
      marginBlockEnd: "300",
      children: [/*#__PURE__*/_jsx(H5, {
        color: "text-core-default",
        marginBlockEnd: "200",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "app.myAccountPopover.settings.profile.title"
        })
      }), /*#__PURE__*/_jsxs(Flex, {
        gap: "200",
        marginBlockEnd: "200",
        children: [/*#__PURE__*/_jsx(View, {
          flexGrow: 1,
          minInlineSize: "0",
          children: /*#__PURE__*/_jsx(Fieldset, {
            _size: "small",
            children: /*#__PURE__*/_jsx(FormControl, {
              label: unescapedText('app.myAccountPopover.settings.profile.firstNameLabel'),
              required: isEditable,
              error: isMissing(firstName),
              children: /*#__PURE__*/_jsx(TextInput, {
                name: "firstName",
                value: val(firstName),
                typography: "body-100",
                readOnly: !isEditable,
                disabled: !isEditable,
                onChange: isEditable ? e => onProfileChange('firstName', e.target.value) : undefined
              })
            })
          })
        }), /*#__PURE__*/_jsx(View, {
          flexGrow: 1,
          minInlineSize: "0",
          children: /*#__PURE__*/_jsx(Fieldset, {
            _size: "small",
            children: /*#__PURE__*/_jsx(FormControl, {
              label: unescapedText('app.myAccountPopover.settings.profile.lastNameLabel'),
              required: isEditable,
              error: isMissing(lastName),
              children: /*#__PURE__*/_jsx(TextInput, {
                name: "lastName",
                value: val(lastName),
                typography: "body-100",
                readOnly: !isEditable,
                disabled: !isEditable,
                onChange: isEditable ? e => onProfileChange('lastName', e.target.value) : undefined
              })
            })
          })
        })]
      }), /*#__PURE__*/_jsx(View, {
        marginBlockEnd: "200",
        flexGrow: 1,
        minInlineSize: "0",
        children: /*#__PURE__*/_jsx(Fieldset, {
          _size: "small",
          children: /*#__PURE__*/_jsx(FormControl, {
            label: unescapedText('app.myAccountPopover.settings.profile.jobTitleLabel'),
            children: /*#__PURE__*/_jsx(TextInput, {
              name: "jobTitle",
              value: val(jobTitle),
              typography: "body-100",
              readOnly: !isEditable,
              disabled: !isEditable,
              onChange: isEditable ? e => onProfileChange('jobTitle', e.target.value) : undefined
            })
          })
        })
      }), /*#__PURE__*/_jsx(View, {
        marginBlockEnd: "200",
        children: /*#__PURE__*/_jsx(Fieldset, {
          _size: "small",
          children: /*#__PURE__*/_jsx(FormControl, {
            label: unescapedText('app.myAccountPopover.settings.profile.phoneLabel'),
            children: /*#__PURE__*/_jsx(TextInput, {
              name: "phone",
              value: val(phone),
              typography: "body-100",
              readOnly: !isEditable,
              disabled: !isEditable,
              onChange: isEditable ? e => onProfileChange('phone', e.target.value) : undefined
            })
          })
        })
      }), isPreferredLanguageEnabled && /*#__PURE__*/_jsxs(View, {
        marginBlockEnd: "200",
        children: [/*#__PURE__*/_jsx(Fieldset, {
          _size: "small",
          children: /*#__PURE__*/_jsx(FormControl, {
            label: unescapedText('app.myAccountPopover.settings.profile.preferredLanguageLabel'),
            help: unescapedText(isLanguageDisabled ? 'app.myAccountPopover.settings.profile.preferredLanguageDisabledHelp' : 'app.myAccountPopover.settings.profile.preferredLanguageHelp'),
            children: /*#__PURE__*/_jsx(HubSpotLanguageSelect, {
              disabled: isLanguageDisabled,
              value: displayLanguage,
              onChange: lang => {
                onLanguageChange(lang.language);
              }
            })
          })
        }), languageError && /*#__PURE__*/_jsxs(Paragraph, {
          color: "text-core-default",
          marginBlock: "0",
          typography: "detail-200",
          children: ["*", languageError]
        })]
      })]
    }), /*#__PURE__*/_jsx(HR, {
      distance: "flush"
    })]
  });
}